/**
 * StarPerformerBanner — iter175 gamification.
 *
 * Celebrates the "Star Performer of the week" on the Job Cards page (shows in
 * Status, Wallet and Mechanic iPad views). Auto-recomputes every Sunday night
 * (the endpoint always reports the most recently completed Mon-Sun week).
 *
 * Gold ⭐ banner   → a mechanic HIT their weekly target (completed >= target).
 * Muted banner     → nobody hit target, so we celebrate the week's top
 *                    performer (highest completed hours) to keep it motivating.
 * Hidden           → no output at all last week.
 */
import { useEffect, useState } from 'react';
import { Trophy, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtRange = (start, end) => {
  try {
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    const opts = { day: 'numeric', month: 'short' };
    return `${s.toLocaleDateString('en-AU', opts)} – ${e.toLocaleDateString('en-AU', opts)}`;
  } catch {
    return `${start} – ${end}`;
  }
};

// Decorative "golden flakes" — small sparkles scattered across the banner.
const FLAKES = [
  { top: '12%', left: '8%', size: 10, delay: '0s' },
  { top: '62%', left: '18%', size: 7, delay: '0.6s' },
  { top: '28%', left: '40%', size: 6, delay: '1.1s' },
  { top: '70%', left: '58%', size: 9, delay: '0.3s' },
  { top: '18%', left: '74%', size: 7, delay: '0.9s' },
  { top: '55%', left: '88%', size: 11, delay: '1.4s' },
];

export default function StarPerformerBanner({ variant = 'full', inline = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/star-performer`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const winner = data.winner;
  const person = winner || data.top_performer;
  if (!person) return null;

  const isStar = Boolean(winner);

  // Compact pill — sits inline on the "Job Cards" title row (iter178).
  if (variant === 'pill') {
    return (
      <div
        data-testid="star-performer-banner"
        title={`${isStar ? 'Star Performer of the Week' : 'Top performer this week'} · ${person.name} · ${Number(person.completed_hours || 0).toFixed(1)}h completed · ${fmtRange(data.week_start, data.week_end)}`}
        className={`inline-flex items-center gap-2 rounded-full pl-1.5 pr-2.5 py-1 border shadow-sm ${
          isStar
            ? 'bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 border-amber-300'
            : 'bg-amber-50 border-amber-200'
        }`}
      >
        <span className={`flex items-center justify-center h-7 w-7 rounded-full flex-shrink-0 ${isStar ? 'bg-white/50' : 'bg-amber-100'}`}>
          <Trophy className={`h-4 w-4 ${isStar ? 'text-amber-700' : 'text-amber-500'}`} />
        </span>
        <span className="flex flex-col leading-tight min-w-0">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-amber-800 whitespace-nowrap">
            {isStar ? '⭐ Star Performer' : 'Top Performer'}
          </span>
          <span className="text-xs font-black text-slate-900 truncate max-w-[140px]" data-testid="star-performer-name">
            {person.name}
          </span>
        </span>
        <span
          className="ml-0.5 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-black text-slate-900 tabular-nums whitespace-nowrap"
          data-testid="star-performer-completed"
        >
          {Number(person.completed_hours || 0).toFixed(1)}h
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="star-performer-banner"
      className={`relative overflow-hidden rounded-xl ${inline ? '' : 'mb-5 w-full'} max-w-[900px] px-5 py-3.5 flex items-center gap-4 border shadow-sm ${
        isStar
          ? 'bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 border-amber-300'
          : 'bg-gradient-to-r from-slate-100 to-amber-50 border-amber-200'
      }`}
    >
      {/* Golden flakes */}
      {isStar && (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {FLAKES.map((f, i) => (
            <Sparkles
              key={i}
              className="absolute text-white/80 animate-pulse"
              style={{ top: f.top, left: f.left, width: f.size, height: f.size, animationDelay: f.delay }}
            />
          ))}
        </div>
      )}

      <div className={`relative flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center ${
        isStar ? 'bg-white/40 ring-2 ring-white/70' : 'bg-amber-100'
      }`}>
        <Trophy className={`h-6 w-6 ${isStar ? 'text-amber-700' : 'text-amber-500'}`} />
      </div>

      <div className="relative flex-1 min-w-0">
        <p className={`text-[11px] font-extrabold uppercase tracking-widest ${isStar ? 'text-amber-800' : 'text-amber-600'}`}>
          {isStar ? '⭐ Star Performer of the Week' : 'Top Performer this week'}
        </p>
        <p className="text-lg sm:text-xl font-black text-slate-900 truncate" data-testid="star-performer-name">
          {person.name}
        </p>
        <p className="text-[11px] text-slate-700/90">
          {fmtRange(data.week_start, data.week_end)}
        </p>
      </div>

      <div className="relative flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <div className="text-center px-2">
          <p className="text-[9px] uppercase tracking-wider text-slate-600 font-bold">Completed</p>
          <p className="text-lg font-black text-slate-900 tabular-nums" data-testid="star-performer-completed">
            {Number(person.completed_hours || 0).toFixed(1)}h
          </p>
        </div>
        <div className="text-center px-2 hidden sm:block">
          <p className="text-[9px] uppercase tracking-wider text-slate-600 font-bold">Target</p>
          <p className="text-lg font-black text-slate-900/70 tabular-nums">
            {Number(person.weekly_target_hours || 0).toFixed(0)}h
          </p>
        </div>
        {isStar ? (
          <span className="rounded-full bg-emerald-600 text-white px-3 py-1 text-[11px] font-bold whitespace-nowrap shadow" data-testid="star-performer-badge">
            Target smashed 🎯
          </span>
        ) : (
          <span className="rounded-full bg-amber-200 text-amber-800 px-3 py-1 text-[11px] font-semibold whitespace-nowrap">
            Chasing target
          </span>
        )}
      </div>
    </div>
  );
}
